// src/components/layout/AuthLayout.js
import React from 'react';
import { Outlet, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Heart, Shield, Clock, ArrowLeft } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';

const AuthLayout = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  
  if (isAuthenticated && user) {
    const roleRoutes = {
      patient: '/patient',
      doctor: '/doctor',
      hospital: '/hospital',
      pharmacy: '/pharmacy',
      driver: '/driver',
      admin: '/admin',
      superadmin: '/admin',
    };
    
    const redirectPath = roleRoutes[user.role] || '/patient';
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      {/* Back to Home Button */}
      <div className="absolute top-3 left-3 sm:top-4 sm:left-4 lg:top-6 lg:left-6">
        <Link
          to="/"
          className="flex items-center space-x-1 sm:space-x-2 text-gray-600 hover:text-primary-600 transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-xs sm:text-sm font-medium">Back to Home</span>
        </Link>
      </div>

      {/* Logo and Brand */}
      <div className="sm:mx-auto w-full sm:max-w-md lg:max-w-2xl">
        <div className="flex justify-center mb-6 sm:mb-8">
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-primary-600 rounded-xl">
              <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">MediGo</h1>
              <p className="text-xs sm:text-sm text-gray-600">Healthcare Platform</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Auth Form Container */}
      <div className="w-full sm:mx-auto sm:max-w-md lg:max-w-2xl xl:max-w-3xl">
        <div className="bg-white py-6 px-4 sm:py-8 sm:px-6 md:px-10 shadow sm:rounded-lg">
          <Outlet />
        </div>
      </div>

      {/* Features Section */}
      <div className="mt-6 sm:mt-8 w-full sm:mx-auto sm:max-w-2xl lg:max-w-4xl px-4">
        <div className="text-center">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Why Choose MediGo?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="text-center p-3 sm:p-4 bg-white rounded-lg shadow-sm">
              <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mx-auto mb-2" />
              <h4 className="font-medium text-sm sm:text-base text-gray-900">Quality Care</h4>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Connect with verified healthcare professionals</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-white rounded-lg shadow-sm">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mx-auto mb-2" />
              <h4 className="font-medium text-sm sm:text-base text-gray-900">Secure Platform</h4>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Your health data is protected and secure</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-white rounded-lg shadow-sm">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mx-auto mb-2" />
              <h4 className="font-medium text-sm sm:text-base text-gray-900">24/7 Access</h4>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Get healthcare services anytime, anywhere</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 sm:mt-8 text-center text-xs sm:text-sm text-gray-500 px-4">
        <p>© 2025 MediGo. All rights reserved.</p>
        <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-2">
          <Link to="/privacy" className="hover:text-primary-600 transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms" className="hover:text-primary-600 transition-colors">
            Terms of Service
          </Link>
          <Link to="/help" className="hover:text-primary-600 transition-colors">
            Help
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;